import React, { useState, useEffect, useRef } from 'react';
import { ImageMetadata } from '../types';
import { shopLocationService } from '../services/shopLocationService';
import AuthenticatedLocationImage from './AuthenticatedLocationImage';
import CropModal from './CropModal';

interface LocationImageManagerProps {
  shopId: string;
  locationId: string;
  images: ImageMetadata[];
  onImagesUpdate: (images: ImageMetadata[]) => void;
  onRotationsUpdate?: (rotations: {[imageId: string]: number}) => void;
  onCropsUpdate?: (crops: {[imageId: string]: { x: number; y: number; width: number; height: number }}) => void;
  onCameraStateChange?: (isActive: boolean, isMobile: boolean) => void;
  className?: string;
}

const MAX_IMAGES_PER_LOCATION = 5;

const LocationImageManager: React.FC<LocationImageManagerProps> = ({
  shopId,
  locationId,
  images,
  onImagesUpdate,
  onRotationsUpdate,
  onCropsUpdate,
  onCameraStateChange,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [localImages, setLocalImages] = useState<ImageMetadata[]>(images);
  const [imageRotations, setImageRotations] = useState<{[imageId: string]: number}>({});
  const [imageCrops, setImageCrops] = useState<{[imageId: string]: { x: number; y: number; width: number; height: number }}>({});
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageId, setCropImageId] = useState<string | null>(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [loadingCropImage, setLoadingCropImage] = useState(false);

  // Camera state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [isMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraFileInputRef = useRef<HTMLInputElement>(null);
  const onCameraStateChangeRef = useRef(onCameraStateChange);
  onCameraStateChangeRef.current = onCameraStateChange;

  // HEIC polling state
  const [heicPolling, setHeicPolling] = useState(false);

  // HEIC polling: periodically re-fetch location data to pick up new image ID after conversion
  useEffect(() => {
    if (!heicPolling) return;
    const interval = setInterval(async () => {
      try {
        const updated = await shopLocationService.getShopLocation(shopId, locationId);
        const oldIds = localImages.map(img => img.id).sort().join(',');
        const newIds = (updated.images || []).map((img: ImageMetadata) => img.id).sort().join(',');
        if (oldIds !== newIds) {
          setHeicPolling(false);
          setLocalImages(updated.images || []);
          onImagesUpdate(updated.images || []);
        }
      } catch { }
    }, 3000);
    const timeout = setTimeout(() => setHeicPolling(false), 120000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [heicPolling, shopId, locationId, localImages, onImagesUpdate]);

  // Camera cleanup on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Notify parent of camera state changes
  useEffect(() => {
    onCameraStateChangeRef.current?.(isCameraActive, isMobile);
  }, [isCameraActive, isMobile]);

  // Attach stream to video element
  useEffect(() => {
    if (isCameraActive && cameraStream) {
      const timer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = cameraStream;
          videoRef.current.play().catch(err => console.error('Failed to play video:', err));
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCameraActive, cameraStream]);

  const startCamera = async () => {
    try {
      let mediaStream: MediaStream | null = null;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
        });
      } catch {
        mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      if (!mediaStream) throw new Error('Failed to acquire camera stream');
      setCameraStream(mediaStream);
      setIsCameraActive(true);
    } catch (err) {
      console.error('Camera error:', err);
      alert('Failed to access camera. Please check permissions or use "Photos".');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        handleImageUpload(file);
      }
    }, 'image/jpeg', 0.92);
  };

  const handleCameraFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleImageUpload(files[0]);
    }
    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  // Update local images when prop changes
  React.useEffect(() => {
    setLocalImages(images);
  }, [images]);

  // Use images in array order (backend maintains order)
  const sortedImages = [...localImages];

  // Check if at maximum capacity
  const atMaxCapacity = sortedImages.length >= MAX_IMAGES_PER_LOCATION;

  const handleDeleteImage = async (imageId: string) => {
    try {
      await shopLocationService.deleteLocationImage(shopId, locationId, imageId);
      const updatedImages = localImages.filter(img => img.id !== imageId);
      setLocalImages(updatedImages);
      onImagesUpdate(updatedImages);
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  const handleImageUpload = async (file: File) => {
    if (sortedImages.length >= MAX_IMAGES_PER_LOCATION) {
      alert(`Maximum of ${MAX_IMAGES_PER_LOCATION} images per location reached. Please delete an image before uploading more.`);
      return;
    }

    try {
      setUploading(true);
      const response = await shopLocationService.uploadLocationImage(shopId, locationId, file);

      // Extract the newly added image from the response
      const updatedLocation = response;
      const newImages = updatedLocation.images || [];

      setLocalImages(newImages);
      onImagesUpdate(newImages);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (files && files[0]) {
      handleImageUpload(files[0]);
    }
  };

  const handleUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      handleFileSelect(target.files);
    };
    input.click();
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...sortedImages];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedImage);

    // Array order is the display order
    setLocalImages(newImages);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    // Update parent with new order
    onImagesUpdate(localImages);
    setDraggedIndex(null);
  };

  const handleRotateLeft = (imageId: string) => {
    const newRotations = {
      ...imageRotations,
      [imageId]: ((imageRotations[imageId] || 0) - 90) % 360
    };
    setImageRotations(newRotations);
    if (onRotationsUpdate) {
      onRotationsUpdate(newRotations);
    }
  };

  const handleRotateRight = (imageId: string) => {
    const newRotations = {
      ...imageRotations,
      [imageId]: ((imageRotations[imageId] || 0) + 90) % 360
    };
    setImageRotations(newRotations);
    if (onRotationsUpdate) {
      onRotationsUpdate(newRotations);
    }
  };

  const handleCropClick = async (imageId: string) => {
    try {
      setLoadingCropImage(true);
      setCropImageId(imageId);

      // Clean up any existing blob URL first
      if (cropImageUrl) {
        URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(null);
      }

      console.log('Fetching location image for crop:', imageId);

      // Fetch the authenticated image and create a blob URL
      // Use undefined size to get the full image for cropping
      const response = await shopLocationService.fetchLocationImage(shopId, locationId, imageId, undefined);

      if (!response.ok) {
        console.error('Image fetch failed:', response.status, response.statusText);
        throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('Image blob loaded:', blob.size, 'bytes, type:', blob.type);

      const objectUrl = URL.createObjectURL(blob);
      console.log('Created object URL:', objectUrl);

      setCropImageUrl(objectUrl);
      setCropModalOpen(true);
    } catch (error) {
      console.error('Error loading image for crop:', error);
      alert(`Failed to load image for cropping. ${error instanceof Error ? error.message : 'Please try again.'}`);
      // Clean up states on error
      setCropImageId(null);
      setCropModalOpen(false);
    } finally {
      setLoadingCropImage(false);
    }
  };

  const handleCropComplete = (cropArea: { x: number; y: number; width: number; height: number }) => {
    if (cropImageId) {
      const newCrops = {
        ...imageCrops,
        [cropImageId]: cropArea
      };
      setImageCrops(newCrops);
      if (onCropsUpdate) {
        onCropsUpdate(newCrops);
      }
    }
    setCropModalOpen(false);
    setCropImageId(null);

    // Clean up the blob URL to prevent memory leaks
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
      setCropImageUrl(null);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setCropImageId(null);

    // Clean up the blob URL to prevent memory leaks
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
      setCropImageUrl(null);
    }
  };

  return (
    <div className={className}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {sortedImages.map((image, index) => (
          <div
            key={image.id}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              position: 'relative',
              width: '120px',
              height: '120px',
              border: draggedIndex === index ? '2px dashed var(--primary)' : '1px solid var(--border-light)',
              borderRadius: '8px',
              overflow: 'hidden',
              cursor: 'move',
              backgroundColor: 'var(--bg-secondary)'
            }}
          >
            <AuthenticatedLocationImage
              shopId={shopId}
              locationId={locationId}
              imageId={image.id}
              alt={`Location image ${index + 1}`}
              onHeicPending={() => setHeicPolling(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: `rotate(${imageRotations[image.id] || 0}deg)`,
                transition: 'transform 0.3s ease'
              }}
              size="thumb_300"
            />

            {/* Rotation and Crop Buttons */}
            <div style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              display: 'flex',
              gap: '4px'
            }}>
              {/* Rotate Left */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRotateLeft(image.id);
                }}
                style={{
                  backgroundColor: 'rgba(0, 123, 255, 0.9)',
                  color: 'white',
                  borderRadius: '4px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'opacity 0.15s ease',
                  opacity: 0.8
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                title="Rotate left 90°"
                aria-label="Rotate left"
              >
                ↶
              </button>

              {/* Rotate Right */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRotateRight(image.id);
                }}
                style={{
                  backgroundColor: 'rgba(0, 123, 255, 0.9)',
                  color: 'white',
                  borderRadius: '4px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'opacity 0.15s ease',
                  opacity: 0.8
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                title="Rotate right 90°"
                aria-label="Rotate right"
              >
                ↷
              </button>

              {/* Crop Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCropClick(image.id);
                }}
                style={{
                  backgroundColor: 'rgba(0, 123, 255, 0.9)',
                  color: 'white',
                  borderRadius: '4px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'opacity 0.15s ease',
                  opacity: 0.8
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                title="Crop image"
                aria-label="Crop image"
              >
                ✂
              </button>

              {/* Delete Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteImage(image.id);
                }}
                style={{
                  backgroundColor: 'var(--error)',
                  color: 'var(--text-inverse)',
                  borderRadius: '4px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'opacity 0.15s ease',
                  opacity: 0.8
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.backgroundColor = 'var(--error)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                  e.currentTarget.style.backgroundColor = 'var(--error)';
                }}
                title="Delete image"
                aria-label="Delete image"
              >
                ×
              </button>
            </div>

            {/* Image order indicator */}
            <div
              style={{
                position: 'absolute',
                bottom: '4px',
                left: '4px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                borderRadius: '4px',
                padding: '2px 6px',
                fontSize: '11px',
                fontWeight: 600
              }}
            >
              {index + 1}
            </div>
          </div>
        ))}

      </div>

      {/* Camera / Photos buttons (replaces old dashed upload button) */}
      {!atMaxCapacity && !isCameraActive && (
        <div style={{ marginTop: '12px' }}>
          <input
            ref={cameraFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleCameraFileSelect}
            style={{ display: 'none' }}
          />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '8px',
            alignItems: 'center'
          }}>
            <button
              type="button"
              onClick={startCamera}
              disabled={uploading}
              style={{
                width: '100%',
                backgroundColor: uploading ? 'var(--disabled-bg)' : 'var(--primary)',
                color: uploading ? 'var(--disabled)' : 'var(--text-inverse)',
                padding: '10px 8px',
                fontSize: '0.8rem',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: uploading ? 'not-allowed' : 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Camera
            </button>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}>OR</div>
            <button
              type="button"
              onClick={() => cameraFileInputRef.current?.click()}
              disabled={uploading}
              style={{
                width: '100%',
                backgroundColor: uploading ? 'var(--disabled-bg)' : 'var(--border-light)',
                color: uploading ? 'var(--disabled)' : 'var(--text-primary)',
                padding: '10px 8px',
                fontSize: '0.8rem',
                borderRadius: '6px',
                border: 'none',
                fontWeight: 600,
                cursor: uploading ? 'not-allowed' : 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {uploading ? 'Uploading...' : 'Photos'}
            </button>
          </div>
          <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'center' }}>
            {sortedImages.length}/{MAX_IMAGES_PER_LOCATION} images
          </div>
        </div>
      )}

      {/* Desktop inline camera */}
      {isCameraActive && !isMobile && (
        <div style={{
          position: 'relative',
          backgroundColor: 'var(--photo-backdrop)',
          borderRadius: '8px',
          overflow: 'visible',
          marginTop: '12px',
          width: '100%',
          maxWidth: '640px'
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: 'auto',
              minHeight: '300px',
              maxHeight: '50vh',
              objectFit: 'cover',
              display: 'block',
              backgroundColor: 'var(--photo-backdrop)'
            }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <button
            type="button"
            onClick={stopCamera}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              fontSize: '1.25rem',
              cursor: 'pointer',
              zIndex: 10
            }}
          >
            &#x2715;
          </button>
          <button
            type="button"
            onClick={capturePhoto}
            disabled={uploading || atMaxCapacity}
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--primary)',
              color: 'var(--text-inverse)',
              borderRadius: '50%',
              border: '4px solid white',
              width: '70px',
              height: '70px',
              fontSize: '1.125rem',
              fontWeight: 600,
              cursor: uploading || atMaxCapacity ? 'not-allowed' : 'pointer',
              opacity: uploading || atMaxCapacity ? 0.5 : 1,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            &#x1F4F7;
          </button>
          {uploading && (
            <div style={{
              position: 'absolute',
              bottom: '100px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500
            }}>
              Uploading...
            </div>
          )}
        </div>
      )}

      {/* Mobile full-screen camera overlay */}
      {isCameraActive && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'var(--photo-backdrop)',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            zIndex: 10
          }}>
            <div style={{ fontSize: '14px', fontWeight: 500 }}>
              {sortedImages.length}/{MAX_IMAGES_PER_LOCATION} images {uploading ? '(uploading...)' : ''}
            </div>
            <button
              onClick={stopCamera}
              type="button"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: 'white',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              &#x2715;
            </button>
          </div>
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                backgroundColor: 'var(--photo-backdrop)'
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <button
              type="button"
              onClick={capturePhoto}
              disabled={uploading || atMaxCapacity}
              style={{
                position: 'absolute',
                bottom: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--primary)',
                color: 'var(--text-inverse)',
                borderRadius: '50%',
                border: '4px solid white',
                width: '70px',
                height: '70px',
                fontSize: '1.125rem',
                fontWeight: 600,
                cursor: uploading || atMaxCapacity ? 'not-allowed' : 'pointer',
                opacity: uploading || atMaxCapacity ? 0.5 : 1,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              &#x1F4F7;
            </button>
            {uploading && (
              <div style={{
                position: 'absolute',
                bottom: '160px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                Uploading...
              </div>
            )}
          </div>
        </div>
      )}

      {atMaxCapacity && (
        <div style={{
          marginTop: '12px',
          fontSize: '13px',
          color: 'var(--text-secondary)',
          fontStyle: 'italic'
        }}>
          Maximum of {MAX_IMAGES_PER_LOCATION} images reached. Delete an image to upload more.
        </div>
      )}

      {/* Crop Modal */}
      {cropModalOpen && cropImageId && cropImageUrl && (() => {
        const image = sortedImages.find(img => img.id === cropImageId);
        if (!image) return null;

        return (
          <CropModal
            imageUrl={cropImageUrl}
            imageName={`Location Image ${sortedImages.indexOf(image) + 1}`}
            onCropComplete={handleCropComplete}
            onCancel={handleCropCancel}
          />
        );
      })()}
    </div>
  );
};

export default LocationImageManager;
